import { SortEnum } from '@/enums'
import { FileType, RelatedFileType } from './file'
import { EffectedUserType, TimestampsType, TranslatableType } from '@/types/general'
import { CategoryType } from '@/types/category'
import { PropertyField, PropertyTranslationsType } from '@/types/property'
import { ModelType } from '@/types/model'
import { VehicleGroupType } from '@/types/vehicle-group'

export type PriceType = {
  net: number
  gross: number
  taxAmount: number
  discountRate: number | null
  originalNet: number | null
  originalGross: number | null
  currencyCode: string
  currencySymbol: string
}

export type ProductTranslationType = {
  title: string
  slug: string
  content?: string
  createdAt: string
  updatedAt: string
}

// Type for product translations in different locales
export type ProductTranslations = {
  [locale: string]: ProductTranslationType
}

export type ProductFieldType = PropertyField & {
  value: string
}

export type ProductPropertyType = {
  id: string
  categoryPropertyId: string
  translations: PropertyTranslationsType
  fields: ProductFieldType[]
}

export type ProductOemType = TimestampsType &
  EffectedUserType & {
    id: string
    vehicleGroup: VehicleGroupType
    model: ModelType
    oem: string
    slug: string // backend Slugify of `oem` (e.g. "1524306 383759" -> "1524306-383759")
    order: number
  }

export type ProductType = TranslatableType &
  EffectedUserType &
  TimestampsType & {
    id: string
    category: CategoryType
    additionalCategories: CategoryType[]
    code: string
    price: PriceType | null
    priceIncludesTax: boolean
    stock: number
    weight?: number
    unitsPerBox?: number
    boxLength?: number
    boxWidth?: number
    boxHeight?: number
    boxDimensionalWeight?: number
    active: boolean
    translations: ProductTranslations
    mainPicture: FileType
    pictures?: RelatedFileType[]
    files?: RelatedFileType[]
    properties: ProductPropertyType[]
    oemNumbers: ProductOemType[]
    repairKits: ProductType[] // Products that are repair kits for this product
    repairedProducts: ProductType[] // Products that this product is a repair kit for
  }

export type ProductsListResponse = {
  page: number
  pages: number
  size: number
  total: number
  items: ProductType[]
}

export enum ProductsSortByEnum {
  CODE = 'code',
  TITLE = 'title',
  PRICE = 'price',
  CREATED_AT = 'createdAt'
}

export type ProductsFilterParams = {
  page?: number
  size?: number
  sort?: SortEnum
  sortBy?: ProductsSortByEnum | string // Allow string for dynamic translation paths like 'translations.en-US.title'
  q?: string
  createdUsers?: string[]
  updatedUsers?: string[]
  createdAtStart?: string
  createdAtEnd?: string
  updatedAtStart?: string
  updatedAtEnd?: string
  active?: string // 'true', 'false', or undefined for all
  random?: boolean // when true, backend returns products in random order (ignores sortBy/sort)
  excludeIds?: string[]
  categoryIds?: string[]
  vehicleGroupIds?: string[]
  brandIds?: string[]
  modelIds?: string[]
  priceMin?: number
  priceMax?: number
  stockMin?: number
  stockMax?: number
}
